import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { SITE, NAV, SERVICES, ZONES } from "../lib/site";

const TH_LINKS = [
  { href: "https://termitehabitat.fr/traitement-termites", label: "Traitement des termites" },
  { href: "https://termitehabitat.fr/traitement-capricorne", label: "Traitement du capricorne" },
  { href: "https://termitehabitat.fr/garantie-5-ans", label: "Garantie 5 ans" },
  { href: "https://termitehabitat.fr/services", label: "Nos services" },
  { href: "https://termitehabitat.fr/secteurs", label: "Secteurs d'intervention" },
  { href: "https://termitehabitat.fr/franchise", label: "Devenir franchisé" },
];

export default function Footer() {
  return (
    <footer className="bg-[#2B2118] text-[#E8DCC9] mt-24" data-testid="site-footer">
      <div className="container-x py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
        <div>
          <img
            src="/brand/logo.png"
            alt="Logo Termite Habitat 33"
            width="80"
            height="80"
            className="w-20 h-20 bg-white p-2 rounded-sm object-contain mb-4"
          />
          <div className="font-serif text-2xl text-white mb-1">Termite Bordeaux</div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#E8DCC9]/60 mb-4">par Termite Habitat 33</div>
          <p className="text-sm text-[#E8DCC9]/80 mb-5">
            Cabinet d'expertise spécialisé dans le traitement des termites, du bois et de la mérule en
            Gironde. Expertises, traitements curatifs et préventifs, intervention sous 48 h en Bordeaux Métropole. Garantie 5 ans : retraitement gratuit en cas de nouvelle infestation.
          </p>
          <div className="space-y-2 text-sm">
            <a href={`tel:${SITE.phoneRaw}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone">
              <Phone className="w-4 h-4" /> {SITE.phone}
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-white" data-testid="footer-email">
              <Mail className="w-4 h-4" /> {SITE.email}
            </a>
            <div className="flex items-start gap-2">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                {SITE.address}<br />
                {SITE.postalCode} {SITE.city.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div>
          <div className="eyebrow text-[#E8DCC9] mb-4">Navigation</div>
          <ul className="space-y-2 text-sm">
            {NAV.map((n) => (
              <li key={n.to}>
                <Link to={n.to} className="hover:text-white" data-testid={`footer-nav-${n.to.replace(/\W/g, "") || "home"}`}>
                  {n.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/devis" className="hover:text-white" data-testid="footer-nav-devis">
                Devis gratuit
              </Link>
            </li>
            <li>
              <Link to="/videos" className="hover:text-white" data-testid="footer-nav-videos">
                Vidéos
              </Link>
            </li>
            <li>
              <Link to="/certifications" className="hover:text-white" data-testid="footer-nav-cert">
                Certifications
              </Link>
            </li>
            <li>
              <Link to="/faq" className="hover:text-white" data-testid="footer-nav-faq">
                FAQ
              </Link>
            </li>
            <li>
              <Link to="/notre-societe" className="hover:text-white" data-testid="footer-nav-ns">
                Notre société
              </Link>
            </li>
            <li>
              <Link to="/salon-medoc-immo-2027" className="text-[#D5A66A] hover:text-white" data-testid="footer-nav-salon">
                🏛️ Salon Médoc Immo 2027
              </Link>
            </li>
            <li>
              <Link to="/mentions-legales" className="hover:text-white" data-testid="footer-nav-legal">
                Mentions légales
              </Link>
            </li>
            <li>
              <a
                href={SITE.googleBusinessUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-white"
                data-testid="footer-nav-gbp"
              >
                ⭐ Fiche Google (5/5)
              </a>
            </li>
          </ul>
        </div>

        <div>
          <div className="eyebrow text-[#E8DCC9] mb-4">Services</div>
          <ul className="space-y-2 text-sm">
            {SERVICES.map((s) => (
              <li key={s.slug}>
                <Link to={`/services/${s.slug}`} className="hover:text-white" data-testid={`footer-service-${s.slug}`}>
                  {s.title}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <div className="eyebrow text-[#E8DCC9] mb-4">Zones</div>
          <ul className="space-y-2 text-sm grid grid-cols-2 gap-x-2">
            {ZONES.slice(0, 12).map((z) => (
              <li key={z.slug}>
                <Link to={`/zones/${z.slug}`} className="hover:text-white" data-testid={`footer-zone-${z.slug}`}>
                  {z.name}
                </Link>
              </li>
            ))}
            <li className="col-span-2 mt-1">
              <Link to="/zones" className="text-white underline underline-offset-4" data-testid="footer-zones-all">
                Voir toutes les zones →
              </Link>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10" data-testid="footer-termitehabitat-block">
        <div className="container-x py-8 flex flex-col lg:flex-row lg:items-center gap-5">
          <a
            href="https://termitehabitat.fr"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-2 font-serif text-lg text-white hover:text-[#D5A66A] transition-colors shrink-0"
            data-testid="footer-termitehabitat-official"
          >
            Site officiel Termite Habitat 33 <ExternalLink className="w-4 h-4" />
          </a>
          <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm">
            {TH_LINKS.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  target="_blank"
                  rel="noopener"
                  className="hover:text-white underline-offset-4 hover:underline"
                  data-testid={`footer-th-${l.href.split("/").pop()}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Bloc SEO local — 100+ requêtes long-tail organiques */}
      <div className="border-t border-white/10 bg-[#241A12]" data-testid="footer-seo-block">
        <div className="container-x py-10 text-[11px] leading-relaxed text-[#E8DCC9]/55">
          <div className="uppercase tracking-widest text-[10px] text-[#D5A66A] mb-3">
            Recherches locales fréquentes
          </div>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Traitement termites Bordeaux</strong> ·{" "}
            <Link to="/services/traitement-termites-bordeaux" className="hover:text-white">entreprise anti termites Bordeaux</Link> ·{" "}
            <Link to="/services/traitement-termites-bordeaux" className="hover:text-white">société traitement termites Bordeaux</Link> ·{" "}
            expert termites Bordeaux ·{" "}
            professionnel termites Gironde ·{" "}
            applicateur anti-termites Bordeaux ·{" "}
            technicien termites Gironde ·{" "}
            urgence termites Bordeaux ·{" "}
            <Link to="/blog/prix-traitement-termites-bordeaux-2026" className="hover:text-white">prix traitement termites Bordeaux</Link> ·{" "}
            tarif injection anti-termites ·{" "}
            coût traitement charpente Bordeaux ·{" "}
            devis termites gratuit Bordeaux
          </p>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Méthodes de traitement</strong> ·{" "}
            injection anti termites Bordeaux ·{" "}
            <Link to="/blog/injection-ou-pieges-traitement-termites" className="hover:text-white">pièges à termites Termigard Bordeaux</Link> ·{" "}
            barrière chimique périmétrique ·{" "}
            bûchage bois infesté ·{" "}
            pulvérisation curative charpente ·{" "}
            traitement termites par injection à cœur ·{" "}
            stations d'appâts Wall Box ·{" "}
            insecticide CTB-P+ ·{" "}
            traitement préventif termites Bordeaux ·{" "}
            <Link to="/services/traitement-preventif" className="hover:text-white">contrat de surveillance termites</Link>
          </p>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Insectes xylophages traités</strong> ·{" "}
            <Link to="/insectes-xylophages/capricornes" className="hover:text-white">traitement capricornes Bordeaux</Link> ·{" "}
            <Link to="/insectes-xylophages/vrillettes" className="hover:text-white">traitement vrillettes bois</Link> ·{" "}
            Hylotrupes bajulus Gironde ·{" "}
            Anobium punctatum ·{" "}
            Xestobium rufovillosum ·{" "}
            lyctus bois ·{" "}
            sirex charpente ·{" "}
            Reticulitermes flavipes Bordeaux ·{" "}
            colonie termites souterrains ·{" "}
            essaimage termites printemps
          </p>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Mérule & humidité</strong> ·{" "}
            <Link to="/services/traitement-merule" className="hover:text-white">traitement mérule Bordeaux</Link> ·{" "}
            Serpula lacrymans Gironde ·{" "}
            champignon lignivore cave ·{" "}
            <Link to="/services/traitement-humidite" className="hover:text-white">traitement humidité cave bordelaise</Link> ·{" "}
            remontées capillaires Bordeaux ·{" "}
            infiltrations cave voûtée ·{" "}
            VMC hygroréglable Bordeaux ·{" "}
            assèchement mur pierre
          </p>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Zones desservies</strong> ·{" "}
            <Link to="/zones/bordeaux" className="hover:text-white">termites Bordeaux centre</Link> ·{" "}
            <Link to="/zones/merignac" className="hover:text-white">Mérignac</Link> ·{" "}
            <Link to="/zones/pessac" className="hover:text-white">Pessac</Link> ·{" "}
            <Link to="/zones/talence" className="hover:text-white">Talence</Link> ·{" "}
            <Link to="/zones/begles" className="hover:text-white">Bègles</Link> ·{" "}
            <Link to="/zones/le-bouscat" className="hover:text-white">Le Bouscat</Link> ·{" "}
            <Link to="/zones/cenon" className="hover:text-white">Cenon</Link> ·{" "}
            <Link to="/zones/lormont" className="hover:text-white">Lormont</Link> ·{" "}
            <Link to="/zones/floirac" className="hover:text-white">Floirac</Link> ·{" "}
            <Link to="/zones/gradignan" className="hover:text-white">Gradignan</Link> ·{" "}
            <Link to="/zones/libourne" className="hover:text-white">Libourne</Link> ·{" "}
            <Link to="/zones/arcachon" className="hover:text-white">Arcachon</Link> ·{" "}
            <Link to="/zones/la-teste-de-buch" className="hover:text-white">La Teste-de-Buch</Link> ·{" "}
            <Link to="/zones/pauillac" className="hover:text-white">Pauillac</Link> ·{" "}
            <Link to="/zones/lege-cap-ferret" className="hover:text-white">Lège-Cap-Ferret</Link>
          </p>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Quartiers Bordeaux</strong> ·{" "}
            termites Chartrons Bordeaux ·{" "}
            termites Saint-Michel Bordeaux ·{" "}
            termites Saint-Pierre Bordeaux ·{" "}
            termites Nansouty Bordeaux ·{" "}
            termites Caudéran ·{" "}
            termites Bacalan ·{" "}
            termites Bastide ·{" "}
            termites Saint-Genès ·{" "}
            termites Victoire Bordeaux ·{" "}
            termites Talence Barrière-de-Toulouse
          </p>
          <p className="mb-3">
            <strong className="text-[#E8DCC9]/80">Questions fréquentes</strong> ·{" "}
            <Link to="/questions-frequentes-locales" className="hover:text-white">FAQ locale Bordeaux Gironde</Link> ·{" "}
            <Link to="/faq" className="hover:text-white">FAQ termites générale</Link> ·{" "}
            <Link to="/blog/comment-savoir-si-maison-bordeaux-termites" className="hover:text-white">comment savoir si termites</Link> ·{" "}
            <Link to="/blog/essaimage-termites-bordeaux-printemps" className="hover:text-white">essaimage termites printemps</Link> ·{" "}
            <Link to="/blog/quartiers-bordeaux-touches-termites" className="hover:text-white">quartiers touchés Bordeaux</Link> ·{" "}
            <Link to="/blog/dangers-termites-maison" className="hover:text-white">dangers termites maison</Link> ·{" "}
            <Link to="/blog/humidite-termites-cave-bordelaise" className="hover:text-white">humidité + termites cave</Link> ·{" "}
            garantie 5 ans anti termites ·{" "}
            avis Termite Habitat 33 ·{" "}
            note Google 5/5 ·{" "}
            expertise gratuite avant devis
          </p>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-x py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-[#E8DCC9]/70">
          <div>
            © {new Date().getFullYear()} {SITE.name} — agence locale Bordeaux Métropole.{" "}
            <Link to="/mentions-legales" className="hover:text-white underline underline-offset-4">Mentions légales</Link>
          </div>
          <div className="text-[#E8DCC9]/55">
            <span>Membre du groupe </span>
            <a
              href={SITE.parentSite}
              target="_blank"
              rel="noopener"
              className="text-[#E8DCC9]/85 hover:text-white"
              data-testid="footer-parent-site-link"
            >
              Termite Habitat 33
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
